// tutara-frontend/src/hooks/useEnterprisePlan.ts
import { useMemo } from 'react';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Reports whether the current tenant is on the ENTERPRISE subscription plan.
 *
 * The backend delivers the plan in the login/OAuth response, so
 * `user.subscriptionPlan` is known synchronously at login (persisted with the
 * session) and is tenant-canonical (see `TenantPlans` / changeset 125 on the
 * backend). We read it first; `currentOrganization.subscriptionPlan` is only a
 * legacy echo of that same tenant plan (organizations no longer carry their own
 * plan column) and is used as a fallback for the window before the org record
 * has synced. The real `SubscriptionPlan` values are lowercase
 * ('free' | 'team' | 'enterprise'); we compare case-insensitively to tolerate
 * backend casing.
 *
 * `isLoading` reflects the auth store's own loading flag; consumers that must
 * avoid flashing the non-enterprise state should render a neutral placeholder
 * while it is true.
 */
export function useEnterprisePlan(): {
  isEnterprise: boolean;
  plan: string | undefined;
  isLoading: boolean;
} {
  const user = useAuthStore((s) => s.user);
  const currentOrganization = useAuthStore((s) => s.currentOrganization);
  const authLoading = useAuthStore((s) => s.isLoading);
  const tenantSynced = useAuthStore((s) => s.tenantSynced);

  return useMemo(() => {
    const plan = user?.subscriptionPlan ?? currentOrganization?.subscriptionPlan;
    const isEnterprise =
      typeof plan === 'string' && plan.toUpperCase() === 'ENTERPRISE';
    // Until `useTenantSync` has refreshed the plan for this session, the cached
    // login-time value may be stale in either direction; report loading so gates
    // render nothing rather than bouncing on a plan that just changed.
    const isLoading = authLoading || (Boolean(user?.tenantId) && !tenantSynced);
    return { isEnterprise, plan, isLoading };
  }, [
    currentOrganization?.subscriptionPlan,
    user?.subscriptionPlan,
    user?.tenantId,
    authLoading,
    tenantSynced,
  ]);
}

export default useEnterprisePlan;
